/**
 * /payments/check-run — AI-assisted AP check run wizard.
 *
 * Step 1: Load open AP invoices → call POST /api/payments/check-run/suggestions
 *         (AI ranks invoices as urgent/normal/defer). Falls back gracefully if no
 *         AI key is configured (503 → shows all invoices without AI rank).
 * Step 2: Select invoices to pay, pick pay date + bank account + period.
 * Step 3: POST /api/payments/check-run → shows created payments.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  apInvoicesListQueryOptions,
  bankAccountBalanceQueryOptions,
  bankAccountsListQueryOptions,
  partiesListQueryOptions,
  periodsListQueryOptions,
  paymentKeys,
} from "@/lib/queries";
import { api } from "@/lib/api";
import type {
  APInvoice,
  BankAccount,
  FiscalPeriod,
  Party,
  PaymentMethod,
  PaymentOut,
} from "@/lib/types";
import { ManualPaymentInstructions } from "@/components/ManualPaymentInstructions";
import { MoneyInput } from "@/components/MoneyInput";

interface CheckRunSearch {
  invoices?: string;
}

export const Route = createFileRoute("/_auth/payments/check-run")({
  component: CheckRunPage,
  validateSearch: (search: Record<string, unknown>): CheckRunSearch => ({
    invoices: typeof search.invoices === "string" ? search.invoices : undefined,
  }),
});

// ── Local types matching the backend schemas ──────────────────────────────────

interface InvoiceSuggestion {
  invoice_id: string;
  include: boolean;
  priority: "urgent" | "normal" | "defer";
  reason: string;
}

interface CheckRunSuggestionResponse {
  summary: string;
  suggestions: InvoiceSuggestion[];
  total_recommended: string;
  ai_notes: string | null;
}

interface CheckRunResult {
  payments: PaymentOut[];
  check_count: number;
  total_amount: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: string | number, currency = "CAD") {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

const PRIORITY_CONFIG: Record<
  string,
  { label: string; cls: string }
> = {
  urgent: {
    label: "Urgent",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
  normal: {
    label: "Normal",
    cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  defer: {
    label: "Defer",
    cls: "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-400",
  },
};

const METHOD_ACTION_HINT: Partial<Record<PaymentMethod, string>> = {
  check: "ready to print",
  ach: "external bank transmission",
  wire: "manual wire instructions",
  zelle: "manual instructions",
  venmo: "manual instructions",
  paypal: "manual instructions",
  crypto: "manual instructions",
  debit_card: "manual instructions",
  credit_card: "manual instructions",
  cash: "record only",
  other: "manual instructions",
};

const MANUAL_METHODS: ReadonlySet<PaymentMethod> = new Set([
  "zelle",
  "venmo",
  "paypal",
  "crypto",
  "wire",
  "debit_card",
  "other",
]);

// ── Main component ────────────────────────────────────────────────────────────

type Step = "select" | "confirm" | "done";

function CheckRunPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const preselectedIds = useMemo(
    () => (search.invoices ? search.invoices.split(",").filter(Boolean) : []),
    [search.invoices],
  );

  const [step, setStep] = useState<Step>("select");

  // Data queries
  const invoicesQ = useQuery(apInvoicesListQueryOptions());
  const bankAccountsQ = useQuery(bankAccountsListQueryOptions());
  const periodsQ = useQuery(periodsListQueryOptions());
  const partiesQ = useQuery(partiesListQueryOptions());

  const partiesById = useMemo(() => {
    const m: Record<string, Party> = {};
    (partiesQ.data ?? []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [partiesQ.data]);

  // Suggestion state
  const [suggestions, setSuggestions] = useState<
    Map<string, InvoiceSuggestion>
  >(new Map());
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiNotes, setAiNotes] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [cashOverride, setCashOverride] = useState<string>("");
  const [useOverride, setUseOverride] = useState(false);

  // Selection state — keyed by invoice_id, value = amount to pay (defaults to balance_due)
  const [selected, setSelected] = useState<Map<string, string>>(new Map());

  // Confirm step fields
  const [payDate, setPayDate] = useState(today());
  const [bankAccountId, setBankAccountId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [currencyCode, setCurrencyCode] = useState("CAD");

  // Result
  const [result, setResult] = useState<CheckRunResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Derive open invoices (posted or partial_paid)
  const openInvoices: APInvoice[] = useMemo(
    () =>
      (invoicesQ.data ?? []).filter(
        (inv) => inv.status === "posted" || inv.status === "partial_paid",
      ),
    [invoicesQ.data],
  );

  // Open fiscal periods
  const openPeriods: FiscalPeriod[] = useMemo(
    () => (periodsQ.data ?? []).filter((p) => p.status === "open"),
    [periodsQ.data],
  );

  // Live balance for the currently-selected bank account
  const balanceQ = useQuery(
    bankAccountBalanceQueryOptions(bankAccountId || undefined),
  );
  const liveBalance = balanceQ.data
    ? parseFloat(balanceQ.data.current_balance)
    : null;
  const overrideValue = useOverride ? parseFloat(cashOverride) : NaN;
  const effectiveBalance =
    useOverride && !isNaN(overrideValue) ? overrideValue : liveBalance;

  // Preselect invoices coming in from /invoices "Pay selected" jump.
  // Runs once after the AP list loads and we have IDs to apply.
  useEffect(() => {
    if (preselectedIds.length === 0) return;
    if (openInvoices.length === 0) return;
    setSelected((prev) => {
      // Don't clobber existing selections (e.g. AI-included ones)
      if (prev.size > 0) return prev;
      const next = new Map<string, string>();
      for (const id of preselectedIds) {
        const inv = openInvoices.find((i) => i.id === id);
        if (!inv) continue;
        const balance = (
          parseFloat(inv.total) - parseFloat(inv.memo_total ?? "0") - parseFloat(inv.paid_amount)
        ).toFixed(2);
        next.set(inv.id, balance);
      }
      return next;
    });
    // Run only when the IDs or the loaded invoice list change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedIds, openInvoices.length]);

  // ── Step 1: AI suggestions ──────────────────────────────────────────────────

  async function fetchSuggestions() {
    setAiLoading(true);
    setAiError(null);

    const invoicePayload = openInvoices.map((inv) => {
      const balance = (
        parseFloat(inv.total) - parseFloat(inv.memo_total ?? "0") - parseFloat(inv.paid_amount)
      ).toFixed(2);
      const dueDate = inv.due_date ?? null;
      const daysOverdue = dueDate
        ? Math.floor(
            (new Date().getTime() - new Date(dueDate).getTime()) /
              86_400_000,
          )
        : null;
      return {
        id: inv.id,
        invoice_no: inv.invoice_no,
        vendor_id: inv.vendor_id,
        vendor_name: inv.vendor_id, // we don't have vendor name on APInvoice; use ID as fallback
        invoice_date: inv.invoice_date,
        due_date: dueDate,
        total: inv.total,
        balance_due: balance,
        currency_code: inv.currency_code,
        days_overdue: daysOverdue,
        discount_available: null,
      };
    });

    try {
      const cashForAi =
        effectiveBalance != null ? effectiveBalance.toFixed(2) : undefined;
      const resp = await api<CheckRunSuggestionResponse>(
        "/api/payments/check-run/suggestions",
        {
          method: "POST",
          body: {
            invoices: invoicePayload,
            available_cash: cashForAi,
            currency_code: currencyCode,
          },
        },
      );

      const map = new Map<string, InvoiceSuggestion>();
      for (const s of resp.suggestions) map.set(s.invoice_id, s);
      setSuggestions(map);
      setAiSummary(resp.summary);
      setAiNotes(resp.ai_notes ?? null);

      // Pre-populate selection based on AI include=true
      const sel = new Map<string, string>();
      for (const s of resp.suggestions) {
        if (s.include) {
          const inv = openInvoices.find((i) => i.id === s.invoice_id);
          if (inv) {
            const balance = (
              parseFloat(inv.total) - parseFloat(inv.memo_total ?? "0") - parseFloat(inv.paid_amount)
            ).toFixed(2);
            sel.set(inv.id, balance);
          }
        }
      }
      setSelected(sel);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 503) {
        setAiError(
          "AI suggestions not available (no Anthropic API key configured). Select invoices manually below.",
        );
      } else {
        const msg =
          (err as { data?: { detail?: string } })?.data?.detail ??
          (err as Error)?.message ??
          "Failed to fetch suggestions";
        setAiError(msg);
      }
    } finally {
      setAiLoading(false);
    }
  }

  // ── Selection helpers ───────────────────────────────────────────────────────

  function toggleInvoice(inv: APInvoice) {
    const balance = (
      parseFloat(inv.total) - parseFloat(inv.memo_total ?? "0") - parseFloat(inv.paid_amount)
    ).toFixed(2);
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(inv.id)) next.delete(inv.id);
      else next.set(inv.id, balance);
      return next;
    });
  }

  function setAmount(invId: string, val: string) {
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(invId, val);
      return next;
    });
  }

  function selectAll() {
    const sel = new Map<string, string>();
    for (const inv of openInvoices) {
      const balance = (
        parseFloat(inv.total) - parseFloat(inv.memo_total ?? "0") - parseFloat(inv.paid_amount)
      ).toFixed(2);
      sel.set(inv.id, balance);
    }
    setSelected(sel);
  }

  function clearAll() {
    setSelected(new Map());
  }

  const selectedTotal = useMemo(() => {
    let sum = 0;
    for (const v of selected.values()) sum += parseFloat(v) || 0;
    return sum;
  }, [selected]);

  // Group selected invoices by vendor's preferred_payment_method.
  // Vendors with no preference fall back to "check".
  const methodBreakdown = useMemo(() => {
    const buckets = new Map<
      PaymentMethod,
      { total: number; vendorIds: Set<string>; count: number }
    >();
    for (const [invId, amt] of selected.entries()) {
      const inv = openInvoices.find((i) => i.id === invId);
      if (!inv) continue;
      const vendor = partiesById[inv.vendor_id];
      const m: PaymentMethod = vendor?.preferred_payment_method ?? "check";
      let b = buckets.get(m);
      if (!b) {
        b = { total: 0, vendorIds: new Set(), count: 0 };
        buckets.set(m, b);
      }
      b.total += parseFloat(amt) || 0;
      b.vendorIds.add(inv.vendor_id);
      b.count += 1;
    }
    return buckets;
  }, [selected, openInvoices, partiesById]);

  // Insufficient-funds check — only meaningful once a bank account has been
  // chosen and a balance has loaded. shortBy > 0 ⇒ total > balance.
  const shortBy =
    effectiveBalance != null
      ? Math.max(0, selectedTotal - effectiveBalance)
      : 0;
  const insufficient = shortBy > 0;

  // ── Step 2 → Step 3 ─────────────────────────────────────────────────────────

  async function submitCheckRun() {
    if (!bankAccountId) { setSubmitError("Please select a bank account."); return; }
    if (!periodId) { setSubmitError("Please select a fiscal period."); return; }
    if (selected.size === 0) { setSubmitError("Select at least one invoice."); return; }

    setSubmitting(true);
    setSubmitError(null);

    const items = Array.from(selected.entries()).map(([ap_invoice_id, amount]) => ({
      ap_invoice_id,
      amount,
    }));

    try {
      const res = await api<CheckRunResult>("/api/payments/check-run", {
        method: "POST",
        body: {
          bank_account_id: bankAccountId,
          payment_date: payDate,
          period_id: periodId,
          items,
          currency_code: currencyCode,
        },
      });
      setResult(res);
      setStep("done");
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as Error)?.message ??
        "Check run failed";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-auto">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate({ to: "/payments" })}
            className="text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          >
            ← Payments
          </button>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            AP Check Run
          </h1>
          {/* Step indicator */}
          <div className="ml-4 flex items-center gap-2">
            {(["select", "confirm", "done"] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <span className="text-slate-300 dark:text-slate-700">›</span>}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    step === s
                      ? "bg-blue-600 text-white"
                      : "bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400"
                  }`}
                >
                  {i + 1}. {s === "select" ? "Select Invoices" : s === "confirm" ? "Confirm" : "Done"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-6xl space-y-6">
        {/* ── STEP: SELECT ── */}
        {step === "select" && (
          <>
            {/* AI suggestions panel */}
            <div className="rounded-lg border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  AI Suggestions
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 flex-1">
                  Get AI recommendations on which invoices to include based on due
                  dates, overdue status, and available cash.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {bankAccountId
                      ? effectiveBalance != null
                        ? `Available cash: ${fmt(effectiveBalance, balanceQ.data?.currency_code ?? currencyCode)}`
                        : "Loading balance…"
                      : "Pick a bank account in step 2 to load balance"}
                  </span>
                  <select
                    value={currencyCode}
                    onChange={(e) => setCurrencyCode(e.target.value)}
                    className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
                  >
                    <option>CAD</option>
                    <option>USD</option>
                    <option>EUR</option>
                  </select>
                  <button
                    onClick={fetchSuggestions}
                    disabled={aiLoading || openInvoices.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                  >
                    {aiLoading ? "Getting suggestions…" : "Get AI Suggestions"}
                  </button>
                </div>
              </div>

              {aiError && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2">
                  {aiError}
                </p>
              )}
              {aiSummary && (
                <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 rounded px-3 py-2 text-sm text-blue-800 dark:text-blue-300">
                  <strong>AI Summary: </strong>{aiSummary}
                  {aiNotes && (
                    <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">{aiNotes}</p>
                  )}
                </div>
              )}
            </div>

            {/* Invoice selection table */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Open AP Invoices
                </h2>
                {invoicesQ.isLoading && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
                )}
                <div className="flex-1" />
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {selected.size} of {openInvoices.length} selected ·{" "}
                  <span className="font-semibold text-slate-700 dark:text-slate-300">
                    {fmt(selectedTotal, currencyCode)}
                  </span>
                </span>
                <button
                  onClick={selectAll}
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  Select all
                </button>
                <button
                  onClick={clearAll}
                  className="text-xs text-slate-500 hover:underline dark:text-slate-400"
                >
                  Clear
                </button>
              </div>

              {openInvoices.length === 0 && !invoicesQ.isLoading && (
                <div className="text-sm text-slate-400 dark:text-slate-500">
                  No open AP invoices available for payment.
                </div>
              )}

              {openInvoices.length > 0 && (
                <div className="rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      <tr>
                        <th className="px-3 py-2.5 w-8" />
                        {suggestions.size > 0 && (
                          <th className="text-left px-3 py-2.5">Priority</th>
                        )}
                        <th className="text-left px-3 py-2.5">Invoice #</th>
                        <th className="text-left px-3 py-2.5">Vendor ID</th>
                        <th className="text-left px-3 py-2.5">Invoice Date</th>
                        <th className="text-left px-3 py-2.5">Due Date</th>
                        <th className="text-right px-3 py-2.5">Balance Due</th>
                        <th className="text-right px-3 py-2.5">Pay Amount</th>
                        {suggestions.size > 0 && (
                          <th className="text-left px-3 py-2.5">AI Reason</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                      {openInvoices.map((inv) => {
                        const balance = (
                          parseFloat(inv.total) - parseFloat(inv.memo_total ?? "0") - parseFloat(inv.paid_amount)
                        ).toFixed(2);
                        const isChecked = selected.has(inv.id);
                        const suggestion = suggestions.get(inv.id);
                        const priorityCfg =
                          suggestion
                            ? PRIORITY_CONFIG[suggestion.priority]
                            : null;
                        const dueDate = inv.due_date;
                        const isOverdue =
                          dueDate
                            ? new Date(dueDate) < new Date()
                            : false;

                        return (
                          <tr
                            key={inv.id}
                            className={`transition-colors ${
                              isChecked
                                ? "bg-blue-50 dark:bg-blue-900/10"
                                : "hover:bg-slate-50 dark:hover:bg-gray-800/50"
                            }`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleInvoice(inv)}
                                className="rounded border-slate-300 dark:border-gray-600"
                              />
                            </td>
                            {suggestions.size > 0 && (
                              <td className="px-3 py-2">
                                {priorityCfg ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityCfg.cls}`}
                                  >
                                    {priorityCfg.label}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 dark:text-slate-500 text-xs">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-2 font-mono text-slate-800 dark:text-slate-200">
                              {inv.invoice_no}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
                              {inv.vendor_id}
                            </td>
                            <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                              {inv.invoice_date}
                            </td>
                            <td
                              className={`px-3 py-2 ${
                                isOverdue
                                  ? "text-red-600 dark:text-red-400 font-semibold"
                                  : "text-slate-600 dark:text-slate-400"
                              }`}
                            >
                              {dueDate ?? "—"}
                              {isOverdue && " ⚠"}
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">
                              {fmt(balance, inv.currency_code)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {isChecked ? (
                                <MoneyInput
                                  currency={inv.currency_code}
                                  value={String(selected.get(inv.id) ?? "")}
                                  onChange={(v) => setAmount(inv.id, v)}
                                  className="border border-slate-300 rounded px-2 py-0.5 text-sm font-mono w-28 text-right bg-white dark:bg-gray-900 dark:border-gray-700"
                                />
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600 font-mono text-sm">—</span>
                              )}
                            </td>
                            {suggestions.size > 0 && (
                              <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 max-w-xs truncate">
                                {suggestion?.reason ?? "—"}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Next button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setStep("confirm")}
                disabled={selected.size === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
              >
                Next: Confirm →
              </button>
            </div>
          </>
        )}

        {/* ── STEP: CONFIRM ── */}
        {step === "confirm" && (
          <>
            <div className="rounded-lg border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-5">
                Check Run Parameters
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={payDate}
                    onChange={(e) => setPayDate(e.target.value)}
                    className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Currency
                  </label>
                  <select
                    value={currencyCode}
                    onChange={(e) => setCurrencyCode(e.target.value)}
                    className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
                  >
                    <option>CAD</option>
                    <option>USD</option>
                    <option>EUR</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Bank Account
                  </label>
                  {bankAccountsQ.isLoading ? (
                    <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
                  ) : (
                    <select
                      value={bankAccountId}
                      onChange={(e) => setBankAccountId(e.target.value)}
                      className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
                    >
                      <option value="">— select bank account —</option>
                      {(bankAccountsQ.data ?? [])
                        .filter((b: BankAccount) => b.is_active)
                        .map((b: BankAccount) => (
                          <option key={b.id} value={b.id}>
                            {b.name} · {b.bank_name}
                            {b.account_number_last4 ? ` ····${b.account_number_last4}` : ""}
                            {" "}({b.currency_code})
                          </option>
                        ))}
                    </select>
                  )}
                  {bankAccountId && (
                    <div className="mt-2 text-xs">
                      {balanceQ.isLoading && (
                        <span className="text-slate-400 dark:text-slate-500">
                          Loading balance…
                        </span>
                      )}
                      {balanceQ.data && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-500 dark:text-slate-400">
                            Available:
                          </span>
                          <span
                            className={`font-mono font-semibold ${
                              insufficient
                                ? "text-red-600 dark:text-red-400"
                                : "text-slate-800 dark:text-slate-200"
                            }`}
                          >
                            {fmt(
                              effectiveBalance ?? 0,
                              balanceQ.data.currency_code,
                            )}
                          </span>
                          <span className="text-slate-400 dark:text-slate-500">
                            · As of{" "}
                            {new Date(balanceQ.data.as_of).toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => balanceQ.refetch()}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            refresh
                          </button>
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-2">
                        <label className="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400">
                          <input
                            type="checkbox"
                            checked={useOverride}
                            onChange={(e) => setUseOverride(e.target.checked)}
                            className="rounded border-slate-300 dark:border-gray-600"
                          />
                          Override
                        </label>
                        {useOverride && (
                          <MoneyInput
                            currency={balanceQ.data?.currency_code ?? currencyCode}
                            value={cashOverride}
                            onChange={setCashOverride}
                            placeholder="Custom available cash"
                            className="border border-slate-300 rounded px-2 py-0.5 text-xs font-mono w-32 bg-white dark:bg-gray-900 dark:border-gray-700"
                          />
                        )}
                      </div>
                      {insufficient && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 font-medium">
                          Insufficient funds: short by{" "}
                          {fmt(shortBy, balanceQ.data?.currency_code ?? currencyCode)}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Fiscal Period
                  </label>
                  {periodsQ.isLoading ? (
                    <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
                  ) : (
                    <select
                      value={periodId}
                      onChange={(e) => setPeriodId(e.target.value)}
                      className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
                    >
                      <option value="">— select period —</option>
                      {openPeriods.map((p) => (
                        <option key={p.id} value={p.id}>
                          Period {p.period_no} · {p.start_date} → {p.end_date}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* By-method breakdown */}
            {methodBreakdown.size > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  By payment method
                  <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
                    based on each vendor's preferred method · vendors with no
                    preference fall back to Check
                  </span>
                </h2>
                <div className="rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-3 py-2">Method</th>
                        <th className="text-right px-3 py-2">Total</th>
                        <th className="text-right px-3 py-2">Vendors</th>
                        <th className="text-right px-3 py-2">Invoices</th>
                        <th className="text-left px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                      {Array.from(methodBreakdown.entries()).map(
                        ([m, b]) => (
                          <tr key={m}>
                            <td className="px-3 py-2 font-medium capitalize text-slate-800 dark:text-slate-200">
                              {m.replace("_", " ")}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {fmt(b.total, currencyCode)}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-600 dark:text-slate-400">
                              {b.vendorIds.size}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-600 dark:text-slate-400">
                              {b.count}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                              {METHOD_ACTION_HINT[m] ?? "—"}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Summary table */}
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Selected Invoices ({selected.size})
              </h2>
              <div className="rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2.5">Invoice #</th>
                      <th className="text-left px-3 py-2.5">Vendor ID</th>
                      <th className="text-left px-3 py-2.5">Due Date</th>
                      <th className="text-right px-3 py-2.5">Pay Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                    {Array.from(selected.entries()).map(([invId, amount]) => {
                      const inv = openInvoices.find((i) => i.id === invId);
                      if (!inv) return null;
                      return (
                        <tr key={invId} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 font-mono text-slate-800 dark:text-slate-200">
                            {inv.invoice_no}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
                            {inv.vendor_id}
                          </td>
                          <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                            {inv.due_date ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">
                            {fmt(amount, inv.currency_code)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 dark:bg-gray-900 border-t border-slate-200 dark:border-gray-700">
                      <td colSpan={3} className="px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400">
                        Total
                      </td>
                      <td className="px-3 py-2 text-right font-bold font-mono text-slate-900 dark:text-slate-100">
                        {fmt(selectedTotal, currencyCode)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {submitError && (
              <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">
                {submitError}
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setStep("select")}
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={submitCheckRun}
                disabled={submitting || !bankAccountId || !periodId || insufficient}
                title={
                  insufficient
                    ? `Selected total exceeds available funds by ${fmt(shortBy, balanceQ.data?.currency_code ?? currencyCode)}. Reduce selection or override the balance.`
                    : undefined
                }
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
              >
                {submitting ? "Creating checks…" : `Create ${selected.size} Check(s)`}
              </button>
            </div>
          </>
        )}

        {/* ── STEP: DONE ── */}
        {step === "done" && result && (
          <>
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5">
              <h2 className="text-base font-semibold text-green-800 dark:text-green-300 mb-1">
                Check run complete
              </h2>
              <p className="text-sm text-green-700 dark:text-green-400">
                Created <strong>{result.check_count}</strong> check(s) totalling{" "}
                <strong>{fmt(result.total_amount, currencyCode)}</strong>.
              </p>
            </div>

            {/* Group payments by method, render manual-method instruction cards */}
            {(() => {
              const byMethod = new Map<PaymentMethod, PaymentOut[]>();
              for (const p of result.payments) {
                const arr = byMethod.get(p.method) ?? [];
                arr.push(p);
                byMethod.set(p.method, arr);
              }
              const sections: React.ReactNode[] = [];
              for (const [m, list] of byMethod.entries()) {
                if (MANUAL_METHODS.has(m)) {
                  sections.push(
                    <div key={m}>
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 capitalize">
                        {m.replace("_", " ")} payments — manual instructions
                        <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
                          send {list.length} payment(s) outside the system, then click "I sent it"
                        </span>
                      </h2>
                      <div className="space-y-3">
                        {list.map((pmt) => (
                          <ManualPaymentInstructions
                            key={pmt.id}
                            payment={pmt}
                            vendor={partiesById[pmt.party_id]}
                            onMarked={() =>
                              queryClient.invalidateQueries({
                                queryKey: paymentKeys.all,
                              })
                            }
                          />
                        ))}
                      </div>
                    </div>,
                  );
                } else {
                  sections.push(
                    <div key={m}>
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 capitalize">
                        {m.replace("_", " ")} payments
                      </h2>
                      <div className="rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                            <tr>
                              <th className="text-left px-3 py-2.5">Payment #</th>
                              <th className="text-left px-3 py-2.5">Check #</th>
                              <th className="text-left px-3 py-2.5">Vendor</th>
                              <th className="text-right px-3 py-2.5">Amount</th>
                              <th className="px-3 py-2.5" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                            {list.map((pmt) => (
                              <tr key={pmt.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                                <td className="px-3 py-2 font-mono text-slate-800 dark:text-slate-200">
                                  {pmt.payment_no}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
                                  {pmt.check_no ?? "—"}
                                </td>
                                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                                  {partiesById[pmt.party_id]?.name ?? (
                                    <span className="font-mono text-xs">
                                      {pmt.party_id}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">
                                  {fmt(pmt.amount, pmt.currency_code)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    onClick={() =>
                                      navigate({
                                        to: "/payments/$paymentId",
                                        params: { paymentId: pmt.id },
                                      })
                                    }
                                    className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>,
                  );
                }
              }
              return <div className="space-y-6">{sections}</div>;
            })()}

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => navigate({ to: "/payments" })}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
              >
                Go to Payments List
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
